import React, { useState } from "react";
import Input from "../components/common/Input";
import Button from "../components/common/Button";
import FormCard, { FormGrid, ActionBar } from "../components/common/FormCard";
import useStore from "../store/useStore";
import api from "../services/api";
import { resolveAssetUrl } from "../utils/assetUrl";

const Profile = () => {
  const { user, updateUser, toast } = useStore();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phone: user?.phone || "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const ch = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  const initials = `${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`.trim() || "U";

  const handleUpdateProfile = async () => {
    if (!form.firstName || !form.lastName || !form.email) {
      toast("Please fill in all required fields", "error");
      return;
    }

    try {
      setLoading(true);
      const updateData = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone
      };

      await api.put(`/users/${user.id}`, updateData);
      
      // Update local user state
      updateUser(updateData);
      
      toast("Profile updated successfully!", "success");
    } catch (error) {
      toast(error.response?.data?.message || "Failed to update profile", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!form.currentPassword || !form.newPassword || !form.confirmPassword) {
      toast("Please fill in all password fields", "error");
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      toast("New passwords do not match", "error");
      return;
    }

    if (form.newPassword.length < 8) {
      toast("New password must be at least 8 characters", "error");
      return;
    }

    try {
      setLoading(true);
      await api.put('/auth/change-password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword
      });

      toast("Password changed successfully!", "success");
      
      // Clear password fields
      setForm(prev => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      }));
    } catch (error) {
      toast(error.response?.data?.message || "Failed to change password", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-3xl border border-teal-100 bg-gradient-to-br from-slate-900 via-teal-900 to-teal-700 text-white shadow-[0_24px_80px_rgba(15,23,42,0.18)]">
        <div className="grid gap-6 px-6 py-8 md:grid-cols-[minmax(0,1.4fr)_minmax(280px,0.9fr)] md:px-8 md:py-10">
          <div className="space-y-4">
            <div className="inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-teal-100">
              Account overview
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight md:text-4xl">Profile Settings</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-teal-50/90 md:text-base">
                Keep your personal details current, refresh your password, and review the account information tied to your workspace.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur-sm">
            <div className="flex items-center gap-4">
              {resolveAssetUrl(user?.companyLogo) ? (
                <img
                  src={resolveAssetUrl(user.companyLogo)}
                  alt="Company logo"
                  className="h-16 w-16 rounded-2xl bg-white object-contain p-2 ring-1 ring-white/15"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 text-2xl font-black text-white ring-1 ring-white/15">
                  {initials}
                </div>
              )}
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-100/80">Signed in as</p>
                <p className="text-lg font-bold text-white">{user?.firstName} {user?.lastName}</p>
                <p className="text-sm text-teal-50/80">{user?.email}</p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl bg-white/10 p-3">
                <p className="text-xs text-teal-100/80">Role</p>
                <p className="mt-1 font-semibold text-white">{user?.role}</p>
              </div>
              <div className="rounded-xl bg-white/10 p-3">
                <p className="text-xs text-teal-100/80">Company ID</p>
                <p className="mt-1 font-semibold text-white">{user?.companyId || "N/A"}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* User Information */}
      <FormCard>
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Personal Information</h3>
          <p className="text-gray-600">Update your personal details</p>
          </div>
          <div className="rounded-full bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-700 ring-1 ring-teal-100">
            Editable profile
          </div>
        </div>

        <FormGrid>
          <Input
            label="First Name"
            name="firstName"
            value={form.firstName}
            onChange={ch}
            placeholder="Enter first name"
            required
          />

          <Input
            label="Last Name"
            name="lastName"
            value={form.lastName}
            onChange={ch}
            placeholder="Enter last name"
            required
          />

          <Input
            label="Email"
            name="email"
            type="email"
            value={form.email}
            onChange={ch}
            placeholder="your@email.com"
            required
          />

          <Input
            label="Phone"
            name="phone"
            value={form.phone}
            onChange={ch}
            placeholder="+220 123 4567"
          />

          <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <span className="text-gray-600">Company:</span>
                <span className="ml-2 font-semibold text-gray-900">{user?.companyName || "—"}</span>
              </div>
              <div>
                <span className="text-gray-600">Role:</span>
                <span className="ml-2 font-semibold text-teal-600">{user?.role}</span>
              </div>
            </div>
          </div>
        </FormGrid>

        <ActionBar>
          <Button 
            variant="primary" 
            icon="update" 
            onClick={handleUpdateProfile} 
            disabled={loading}
          >
            Update Profile
          </Button>
        </ActionBar>
      </FormCard>

      {/* Change Password */}
      <FormCard>
        <div className="mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Change Password</h3>
          <p className="text-gray-600">Update your password to keep your account secure</p>
        </div>

        <FormGrid>
          <Input
            label="Current Password"
            name="currentPassword"
            type="password"
            required
            value={form.currentPassword}
            onChange={ch}
            placeholder="Enter current password"
          />

          <div></div>

          <Input
            label="New Password"
            name="newPassword"
            type="password"
            required
            value={form.newPassword}
            onChange={ch}
            placeholder="Min. 8 characters"
          />

          <Input
            label="Confirm New Password"
            name="confirmPassword"
            type="password"
            required
            value={form.confirmPassword}
            onChange={ch}
            placeholder="Re-enter new password"
          />
        </FormGrid>

        <ActionBar>
          <Button 
            variant="success" 
            onClick={handleChangePassword} 
            disabled={loading}
          >
            Change Password
          </Button>
        </ActionBar>
      </FormCard>

      {/* Account Information */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Account Information</h3>
        <div className="grid gap-4 text-sm md:grid-cols-2">
          <div className="flex justify-between rounded-xl bg-gray-50 px-4 py-3">
            <span className="text-gray-600">Account ID:</span>
            <span className="font-semibold text-gray-900">{user?.id}</span>
          </div>
          <div className="flex justify-between rounded-xl bg-gray-50 px-4 py-3">
            <span className="text-gray-600">Company ID:</span>
            <span className="font-semibold text-gray-900">{user?.companyId}</span>
          </div>
          <div className="flex justify-between rounded-xl bg-gray-50 px-4 py-3">
            <span className="text-gray-600">Role:</span>
            <span className="font-semibold text-teal-600">{user?.role}</span>
          </div>
          <div className="flex justify-between rounded-xl bg-gray-50 px-4 py-3">
            <span className="text-gray-600">Email:</span>
            <span className="font-semibold text-gray-900">{user?.email || "—"}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;